import { useState, useCallback } from "react";
import { testimonialsData } from "@/mocks/homeData";
import AnimatedSection from "@/components/AnimatedSection";

export default function TestimonialsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const items = testimonialsData.items;

  const goTo = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const next = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const prev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  const activeItem = items[activeIndex];

  return (
    <section className="py-20 md:py-28 bg-accent-50">
      <div className="container-astro">
        {/* Heading */}
        <AnimatedSection>
          <div className="text-center mb-14 md:mb-18">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
              {testimonialsData.heading}
            </h2>
            <div className="w-20 h-1.5 bg-accent-500 rounded-full mx-auto" />
          </div>
        </AnimatedSection>

        {/* Main testimonial card */}
        <AnimatedSection delay={150}>
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              {/* Prev arrow - vertical midpoint, outside left edge */}
              <button
                onClick={prev}
                className="absolute left-0 lg:-left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white border border-background-200/80 text-foreground-500 shadow-sm hover:text-accent-600 hover:border-accent-300/50 hover:-translate-x-0.5 transition-all duration-300"
                aria-label="Previous testimonial"
              >
                <i className="ri-arrow-left-line text-lg md:text-xl" />
              </button>
              {/* Next arrow - vertical midpoint, outside right edge */}
              <button
                onClick={next}
                className="absolute right-0 lg:-right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white border border-background-200/80 text-foreground-500 shadow-sm hover:text-accent-600 hover:border-accent-300/50 hover:translate-x-0.5 transition-all duration-300"
                aria-label="Next testimonial"
              >
                <i className="ri-arrow-right-line text-lg md:text-xl" />
              </button>

              <div
                key={activeIndex}
                className="relative bg-white rounded-2xl md:rounded-3xl border border-background-200/80 p-8 md:p-12 pt-10 md:pt-12 testimonial-slide"
              >
                {/* Large light-blue double quote marks */}
                <div className="absolute top-6 left-8 md:top-8 md:left-10 text-6xl md:text-7xl font-serif text-accent-300 leading-none select-none">
                  &ldquo;&ldquo;
                </div>

                {/* Gold stars */}
                <div className="flex items-center justify-center gap-1.5 mb-6">
                  {Array.from({ length: activeItem.rating }).map((_, i) => (
                    <span key={i} className="text-amber-400 text-lg md:text-xl">
                      <i className="ri-star-fill" />
                    </span>
                  ))}
                </div>

                {/* Centered testimonial text */}
                <p className="text-center text-base md:text-lg text-foreground-700 leading-relaxed mb-8 relative z-10">
                  {activeItem.text}
                </p>

                {/* Client info - bottom-left: real photo with name & title below */}
                <div className="mt-4 flex flex-col items-start gap-3">
                  <img
                    src={activeItem.image}
                    alt={`Portrait of ${activeItem.name}, ${activeItem.position} at ${activeItem.company}`}
                    className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover object-top border-2 border-accent-200/70"
                  />
                  <div>
                    <p className="text-sm md:text-base font-bold text-foreground-950 font-heading">
                      {activeItem.name}
                    </p>
                    <p className="text-xs md:text-sm text-foreground-500">
                      {activeItem.position}, {activeItem.company}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Circular dot indicators */}
            <div className="flex items-center justify-center gap-2.5 mt-8">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`transition-all duration-300 rounded-full ${
                    i === activeIndex
                      ? "w-3 h-3 bg-accent-500 scale-110"
                      : "w-3 h-3 bg-background-300 hover:bg-background-400"
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}